import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';

import { ThirdPartyAccount } from '@app/client/settings/shared/modules/integrations/integrations.model';
import { params } from '@app/configurations';
import { STORAGE_KEYS } from '@app/core/models/storage-keys.models';
import { userRoleToAccessLevel } from '@app/core/utilities/api.utilities';
import { assertArray } from '@app/core/utilities/array.utilities';
import {
  customerHasFeatureEnabled,
  customerHasNotificationsFeatureEnabled,
  customersHasCHEnterpriseFeature,
  customersHasMinBid,
  customersHasPGSecondGoal,
  isCustomerFull,
  isEssentialSegment,
  isPreTrial,
  preserveLastUsedCustomerID,
} from '@app/core/utilities/customer.utilities';
import { IsCurrentDayinlast3Days, SplitDatesFromUrl } from '@app/core/utilities/date.utilities';
import { getAccessRoles, hasAccess } from '@app/core/utilities/user.utilities';
import { addDays } from 'date-fns';
import { AccountsApiService } from '../../../api/accounts-api.service';
import { CustomerApiService } from '../../../api/customer-api.service';
import { AdPlatform, CampaignOptimizationValue, CampaignType, CustomerSummary } from '../../../models/common.models';
import {
  AdspertPlanType,
  CancellationStatus,
  Currency,
  Customer,
  CustomerFeature,
  CustomerSegment,
  InvoiceCurrency,
  SubscriptionStatus,
} from '../../../models/customer.models';
import { UserRole, UserSetting } from '../../../models/user.models';
import { SetMetricCurrency, SetSubscriptionCurrency, SetUserCurrency } from '../currency/currency-state.actions';
import { CurrencyState } from '../currency/currency.state';
import { DefaultGlobalFilters } from '../global-filters/global-filters-state.actions';
import { RouterNavState } from '../router-nav/router-nav.state';
import { UserState } from '../user/user.state';
import { GetCustomer, GetThirdPartyAccounts, ReloadCustomer, UpdateCustomer } from './customer-state.actions';
import { CUSTOMER_STATE_TOKEN, CustomerStateModel } from './customer-state.models';
@Injectable()
@State<CustomerStateModel>({
  name: CUSTOMER_STATE_TOKEN,
  defaults: { userCurrency: 'EUR' },
})
export class CustomerState {
  @Selector([CustomerState.customer, UserState.isSysAdmin])
  static isAgencyAdmin(details: Customer, isSysAdmin: boolean): boolean {
    return IsAgencyAdmin(details, isSysAdmin);
  }

  @Selector()
  static hasRasCampaigns({ summary }: CustomerStateModel): boolean {
    return !!summary?.hasAmzRasCampaigns;
  }

  @Selector()
  static isBillingSubsidiary({ details }: CustomerStateModel): boolean {
    return details?.type === 'BILLING-SUBSIDIARY';
  }

  @Selector()
  static isAgencyClient({ details }: CustomerStateModel): boolean {
    return IsAgencyClient(details);
  }

  @Selector()
  static customerIsAgency({ details }: CustomerStateModel): boolean {
    return !!(details?.type === 'MAIN' && (details?.agencyAdminsCount || details?.agencyClientsCount));
  }

  @Selector()
  static isAccountGroup({ details }: CustomerStateModel): boolean {
    return !!details?.mainCustomerId;
  }

  @Selector([CustomerState.customer, UserState.isSysAdmin])
  static isAgency(details: Customer, isSysAdmin: boolean): boolean {
    return IsAgencyAdmin(details, isSysAdmin) || IsAgencyClient(details);
  }

  @Selector([CustomerState.summary])
  static hasVCPMCampaign(summary): boolean {
    return summary.campaignOptimizationTypes.includes('VCPM');
  }

  @Selector()
  static customer(customer: CustomerStateModel): Customer {
    return customer.details;
  }

  @Selector()
  static customerCreatedAt(customer: CustomerStateModel): Date {
    return new Date(customer.details?.createdAt || Date.now());
  }

  @Selector()
  static customerIsPreTrial(customer: CustomerStateModel): boolean {
    return isPreTrial(customer.details);
  }

  @Selector()
  static summary(customer: CustomerStateModel): CustomerSummary {
    return customer.summary;
  }

  @Selector()
  static thirdPartyAccounts(customer: CustomerStateModel): ThirdPartyAccount[] {
    return assertArray(customer.thirdPartyAccounts);
  }

  @Selector()
  static campaignOptimizationTypes(customer: CustomerStateModel): CampaignOptimizationValue[] {
    return customer.summary?.campaignOptimizationTypes || [];
  }

  @Selector()
  static campaignTypes(customer: CustomerStateModel): CampaignType[] {
    return assertArray(customer?.summary?.campaignTypes);
  }

  @Selector()
  static campaignProfitSupported(customer: CustomerStateModel): boolean {
    return !!customer.summary?.campaignProfitSupported;
  }

  @Selector()
  static campaignRevenueSupported(customer: CustomerStateModel): boolean {
    return !!customer.summary?.campaignRevenueSupported;
  }

  @Selector()
  static platformsAccessible(customer: CustomerStateModel): AdPlatform[] {
    return customer.summary?.accountPlatforms || [];
  }

  @Selector()
  static customerHasAmazonAccount(customer: CustomerStateModel): boolean {
    return customer.summary?.accountPlatforms.includes('AMAZON');
  }

  @Selector([CustomerState.summary])
  static customerHasOpTypes(summary: CustomerSummary): boolean {
    return summary.campaignOptimizationTypes.length > 0;
  }

  @Selector()
  static customerId(customer: CustomerStateModel): number | undefined {
    return customer.details?.customerId;
  }

  // deprecated, use directly from cureency.state.ts
  @Selector([CurrencyState.rates])
  static CurrencyRates(currencyRates) {
    return currencyRates;
  }
  @Selector([CurrencyState.subscriptionCurrency])
  static subscriptionCurrency(val): InvoiceCurrency {
    return val;
  }
  @Selector([CurrencyState.customerCurrency])
  static customerCurrency(val): Currency {
    return val;
  }
  @Selector([CurrencyState.userCurrency])
  static userCurrency(val): Currency {
    return val;
  }

  @Selector()
  static customerName(customer: CustomerStateModel): string {
    return customer.details?.name;
  }

  @Selector()
  static customersCount(customer: CustomerStateModel): number {
    return customer.customersCount;
  }

  @Selector()
  static customersHasPGSecondGoal(customer: CustomerStateModel): boolean {
    return customersHasPGSecondGoal(customer.details);
  }

  @Selector()
  static customersHasNotificationsFeature(customer: CustomerStateModel): boolean {
    return customerHasNotificationsFeatureEnabled(customer.details);
  }

  @Selector()
  static customersHasSearchOptimizationFeature(customer: CustomerStateModel): boolean {
    return customerHasFeatureEnabled(customer.details, CustomerFeature.SearchOptimization);
  }

  @Selector()
  static customersHasCHEnterpriseFeature(customer: CustomerStateModel): boolean {
    return customersHasCHEnterpriseFeature(customer.details);
  }

  @Selector([CustomerState.customer])
  static customersHasMinBid(customer: Customer): boolean {
    return customersHasMinBid(customer);
  }

  @Selector()
  static access(customer: CustomerStateModel): UserRole | undefined {
    return customer.details?.access;
  }

  @Selector()
  static canEnterAccountInformation(customer: CustomerStateModel): boolean {
    return ['AGENCY-ADMIN', 'CUSTOMER-ADMIN', 'SYS-ADMIN'].includes(customer.details?.access);
  }

  @Selector()
  static userAccessRoles(customer: CustomerStateModel): UserRole[] {
    return getAccessRoles(customer?.details?.access);
  }

  @Selector()
  static isDemo(customer: CustomerStateModel): boolean {
    return !!customer?.details?.isDemo;
  }

  @Selector([CustomerState.customer, CustomerState.userAccessRoles])
  static selectablePermissionRole(details: Customer, roles: UserRole[]): UserRole[] {
    return roles.filter((role) => {
      if (role === 'SYS-ADMIN') {
        return false;
      } else if (role === 'AGENCY-ADMIN') {
        return details?.type === 'MAIN' && (details?.access === 'AGENCY-ADMIN' || details?.access === 'SYS-ADMIN');
      }
      return true;
    });
  }

  @Selector()
  static hasNotificationsAccess(customer: CustomerStateModel): boolean | undefined {
    return customerHasNotificationsFeatureEnabled(customer?.details);
  }

  @Selector([CustomerState.customer])
  static canStartSubscription(customer: Customer): boolean {
    return !!customer?.trial || !!customer?.trialStart || customer.canceled || !!customer.canceledDue;
  }

  @Selector([CustomerState.access])
  static hasCustomerAdminRights(access: UserRole): boolean {
    return hasAccess(access, 'CUSTOMER-ADMIN');
  }

  @Selector([CustomerState.access])
  static hasAgencyAdminRights(access: UserRole): boolean {
    return hasAccess(access, 'AGENCY-ADMIN');
  }

  @Selector([CustomerState.customer])
  static segment(customer: Customer): CustomerSegment {
    return customer?.segment;
  }

  @Selector([CustomerState.customer])
  static isEssentialSegment(customer: Customer): boolean {
    return isEssentialSegment(customer);
  }

  @Selector([CustomerState.customer])
  static isEssentialPlan(customer: Customer): boolean {
    return customer?.planType === AdspertPlanType.Essential;
  }

  @Selector([CustomerState.customer])
  static isDefaultPricingSegment(customer: Customer): boolean {
    return [CustomerSegment.Essential, CustomerSegment.Scale, CustomerSegment.Pro].includes(customer?.segment);
  }

  @Selector([CustomerState.customer])
  static isManagedSegment(customer: Customer): boolean {
    return customer?.planType == null && customer?.segment && customer.segment !== CustomerSegment.Essential;
  }

  @Selector([CustomerState.customer, CustomerState.isEssentialSegment])
  static disallowStartOptimization(customer: Customer, isEssentialSegment: boolean): boolean {
    return !customer?.allowStartOptimization && !isEssentialSegment;
  }

  @Selector([CustomerState.customer])
  static canceled(customer: Customer): boolean {
    return !!customer?.canceled;
  }

  @Selector([CustomerState.customer])
  static cancellationDate(customer: Customer): Date | undefined {
    return customer?.canceledDue && new Date(customer.canceledDue);
  }

  @Selector([CustomerState.customer])
  static subscriptionEnd(customer: Customer): Date | undefined {
    return customer?.subscriptionEnd && new Date(customer.subscriptionEnd);
  }

  @Selector([CustomerState.customer])
  static isTrial(customer: Customer): boolean {
    return customer?.trial;
  }

  @Selector([CustomerState.customer])
  static subscriptionStatus(customer: Customer): SubscriptionStatus | undefined {
    return customer?.subscriptionStatus;
  }

  @Selector([CustomerState.customer, CustomerState.summary])
  static isCustomerFull(customer: Customer, summary: CustomerSummary): boolean {
    return isCustomerFull(customer, summary);
  }

  @Selector([RouterNavState.queryParam(params.GLOBAL_FILTER_DATE_RANGE), CustomerState.customerHasAmazonAccount])
  static showAmz3DayDataSyncNote(dateParams, hasAMZaccount): boolean {
    let dates;
    try {
      dates = SplitDatesFromUrl(dateParams);
    } catch (error) {
      dates = [addDays(new Date(), -31), addDays(new Date(), -1)];
    }

    return hasAMZaccount && IsCurrentDayinlast3Days(dates);
  }

  @Selector([CustomerState.cancellationDate, CustomerState.isAccountGroup, CustomerState.subscriptionStatus])
  static endingOrEndedSubscription(
    canceled: Date,
    isAccountGroup: boolean,
    subscriptionStatus: SubscriptionStatus,
  ): boolean {
    const hasActive = subscriptionStatus === SubscriptionStatus.Active;
    const hasEnding = subscriptionStatus === SubscriptionStatus.Ending;
    const hasTerminated = subscriptionStatus === SubscriptionStatus.Terminated;

    return hasActive ? hasEnding || hasTerminated : !!canceled && !isAccountGroup;
  }

  @Selector([CustomerState.endingOrEndedSubscription, CustomerState.subscriptionEnd, CustomerState.cancellationDate])
  static subscriptionCancellationDate(
    endingOrEnded: boolean,
    endDate: Date | undefined,
    cancellationDate: Date | undefined,
  ): Date | undefined {
    return endingOrEnded ? endDate || cancellationDate : undefined;
  }

  @Selector([
    CustomerState.hasCustomerAdminRights,
    CustomerState.isEssentialSegment,
    CustomerState.endingOrEndedSubscription,
    CustomerState.subscriptionStatus,
    CustomerState.isManagedSegment,
  ])
  static subscriptionCancellationStatus(
    isAdmin: boolean,
    isEssentialSegment: boolean,
    endingOrEnded: boolean,
    subscriptionStatus: SubscriptionStatus,
    isManaged: boolean,
  ): CancellationStatus {
    const canUndo = subscriptionStatus === SubscriptionStatus.Ending;

    let status = CancellationStatus.None;

    if (endingOrEnded) {
      if (!isAdmin) {
        status ||= CancellationStatus.User;
      }

      if (isManaged || !isEssentialSegment) {
        status ||= CancellationStatus.Managed;
      }

      if (canUndo) {
        status ||= CancellationStatus.Undo;
      }

      status ||= subscriptionStatus ? CancellationStatus.Restart : CancellationStatus.Start;
    }

    return status;
  }

  constructor(
    private store: Store,
    private ca: CustomerApiService,
    private accountsApiService: AccountsApiService,
  ) {}

  @Action(GetCustomer)
  getCustomer({ setState, dispatch }: StateContext<CustomerStateModel>): Observable<unknown> {
    return this.store.selectOnce(RouterNavState.queryParams).pipe(
      map((queryParams) => ({
        customerId: queryParams?.[params.CUSTOMER_ID],
        adspertId: queryParams?.[params.ADSPERT_ID],
        impersonationId: queryParams?.[params.IMPERSONATION_ID],
      })),
      switchMap(({ customerId, adspertId, impersonationId }) => {
        // fetch adspert id when server redirects with oauth/account
        // update adspertID if there's one
        if (['oauth/account', 'oauth/amazon-sp'].some((path) => location.pathname.includes(path))) {
          adspertId = location.pathname.split('/').reverse()[1];
        }

        if (!customerId && location.pathname.startsWith('/payment-methods/')) {
          customerId = location.pathname.split('/')[2];
        }

        if (!customerId && adspertId) {
          return this.accountsApiService.getAccountById(adspertId).pipe(
            map((customer) => customer.customerId),
            catchError(() => of(void 0)),
          );
        } else {
          const storedCustomerId = !impersonationId ? localStorage.getItem(STORAGE_KEYS.CUSTOMER_ID) : null;
          customerId = parseInt(customerId || storedCustomerId, 10) || void 0;
        }

        return of(customerId);
      }),
      switchMap((customerId?: number) => (customerId ? of(customerId) : this.ca.getFirstCustomer())),
      switchMap((customer) => (typeof customer === 'number' ? this.ca.getCustomerById(customer) : of(customer))),
      switchMap((customer) => {
        if (customer === undefined) {
          localStorage.removeItem(STORAGE_KEYS.CUSTOMER_ID);
          setState(patch({ details: undefined, customersCount: undefined, summary: undefined }));
          return of(undefined);
        } else {
          if (!this.store.selectSnapshot(RouterNavState.impersonationId)) {
            preserveLastUsedCustomerID(customer);
          }
          return of(customer).pipe(
            switchMap((customer) =>
              hasAccess(customer.access, 'CUSTOMER-ADMIN')
                ? this.ca.getCustomerById(customer.customerId, userRoleToAccessLevel(customer.access))
                : of(customer),
            ),
            switchMap((customer) =>
              forkJoin([
                this.ca.getCustomersCount(),
                this.ca.getCustomerSummary(customer.customerId),
                this.ca.getCustomerThirdPartyAccounts(customer.customerId),
                this.store.selectOnce(UserState.setting(UserSetting.General)),
              ]).pipe(
                tap(([customersCount, summary, thirdPartyAccounts, settings]) => {
                  dispatch([
                    new DefaultGlobalFilters({ showAllAccounts: isPreTrial(customer) }),
                    new SetUserCurrency(settings?.currency || customer?.metricCurrency || 'EUR'),
                    new SetMetricCurrency(customer?.metricCurrency),
                    new SetSubscriptionCurrency(customer?.subscriptionCurrency || 'EUR'),
                  ]);

                  setState(
                    patch({
                      details: customer,
                      customersCount,
                      thirdPartyAccounts,
                      summary: {
                        ...summary,
                        campaignOptimizationTypes: summary.campaignOptimizationTypes,
                      },
                    }),
                  );
                }),
              ),
            ),
          );
        }
      }),
    );
  }

  @Action(ReloadCustomer)
  reloadCurrentCustomer({ dispatch }: StateContext<Customer>): Observable<unknown> {
    return dispatch(new GetCustomer());
  }

  @Action(UpdateCustomer)
  updateCustomer({ dispatch }: StateContext<Customer>, { update }: UpdateCustomer): Observable<unknown> {
    return this.ca.updateCustomer(update).pipe(switchMap(() => dispatch(new ReloadCustomer())));
  }

  @Action(GetThirdPartyAccounts)
  getThirdPartyAccounts({ patchState }: StateContext<CustomerStateModel>): Observable<unknown> {
    return this.ca.getCustomerThirdPartyAccounts().pipe(
      map((thirdPartyAccounts) => {
        patchState({ thirdPartyAccounts });
      }),
    );
  }
}

const IsAgencyClient = (details) => details?.type === 'AGENCY-CLIENT';

const IsAgencyAdmin = (details: Customer, isSysAdmin: boolean) =>
  details?.access === 'AGENCY-ADMIN' || (isSysAdmin && !!(details?.agencyAdminsCount || details?.agencyClientsCount));
